import React, { useEffect, useRef, useState } from "react";
import InfoCiclo from "./InfoCiclo";
import i18n from "../i18n/index.js";

import Carousel from "react-elastic-carousel";
import "../scss/Video.scss";

const URL_VID = "/assets/video.mp4";
const URL_VID_BACK = "/assets/reversed.mp4";

// Segundo del video (hacia adelante) en el que se detiene cada estación.
// La última estación (día 160) termina al final del video.
const TIME_CODES = [
  0, 1, 4.6, 5.5, 7.6, 14.4, 16, 18.9, 19.9, 20.4, 20.6, 23, 25,
];

const DAYS = [
  { day: 0, title: "lyfecycle.day00.title" },
  { day: 1, title: "lyfecycle.day01.title" },
  { day: 12, title: "lyfecycle.day12.title" },
  { day: 15, title: "lyfecycle.day15.title" },
  { day: 18, title: "lyfecycle.day18.title02" },
  { day: 35, title: "lyfecycle.day35.title02" },
  { day: 40, title: "lyfecycle.day40.title02" },
  { day: 50, title: "lyfecycle.day50.title02" },
  { day: 60, title: "lyfecycle.day60.title02" },
  { day: 70, title: "lyfecycle.day70.title02" },
  { day: 80, title: "lyfecycle.day80.title02" },
  { day: 85, title: "lyfecycle.day85.title02" },
  { day: 120, title: "lyfecycle.day120.title02" },
  { day: 160, title: "lyfecycle.day160.title02" },
];

const LAST_STATION = DAYS.length - 1;

const DOTS_ALWAYS = 17;
const DOTS_DESKTOP = 23;

/*
 * Controla dos <video>: uno hacia adelante y otro con el mismo clip invertido.
 * Para "retroceder" se reproduce el invertido desde el punto equivalente
 * (duración - tiempo) y se lo pone por encima. Siempre hay un único video
 * activo, un único tiempo objetivo y un único loop que lo vigila.
 */
function createPlayer(vid, vidBack) {
  const objectUrls = [];
  const requests = [];
  let readyCount = 0;
  let disposed = false;
  let active = vid;
  let target = 0;
  let rafId = null;
  let switchToken = 0;
  let pendingStation = null;

  const isReady = () => readyCount === 2;
  const backTime = (forwardTime) => Math.max(0, vidBack.duration - forwardTime);
  const forwardTimeOf = (s) =>
    s < TIME_CODES.length ? TIME_CODES[s] : vid.duration;

  const setBackground = (s) => {
    const url = `url('/assets/planta${s}.jpg')`;
    [vid, vidBack].forEach((v) => {
      v.style.backgroundImage = url;
      v.style.backgroundSize = "200px";
      v.style.backgroundPosition = "center";
      v.style.backgroundRepeat = "no-repeat";
    });
  };

  const show = (video) => {
    vid.style.zIndex = video === vid ? 1 : 0;
    vidBack.style.zIndex = video === vidBack ? 1 : 0;
  };

  const stopWatching = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = null;
  };

  const reachedTarget = () => {
    if (active.paused) return true;
    if (!active.ended && active.currentTime < target - 0.02) return false;
    active.pause();
    // Si se pasó (pestaña en segundo plano, frame lento), vuelve al punto exacto.
    if (Math.abs(active.currentTime - target) > 0.05) active.currentTime = target;
    stopWatching();
    return true;
  };

  // requestAnimationFrame para frenar justo en la estación; "timeupdate"
  // (~4 veces por segundo) queda de respaldo porque rAF se frena en
  // pestañas en segundo plano.
  const watch = () => {
    rafId = null;
    if (!reachedTarget()) rafId = requestAnimationFrame(watch);
  };

  const onTimeUpdate = (e) => {
    if (e.target === active && rafId !== null) reachedTarget();
  };

  const playActive = () => {
    const playing = active.play();
    // play() se rechaza si otro clic lo interrumpe con pause(); no es un error.
    if (playing) playing.catch(() => {});
    stopWatching();
    rafId = requestAnimationFrame(watch);
  };

  const goTo = (requested) => {
    const next = Math.min(Math.max(requested, 0), LAST_STATION);
    if (disposed) return;
    if (!isReady()) {
      pendingStation = next;
      return;
    }

    const position =
      active === vid ? vid.currentTime : vidBack.duration - vidBack.currentTime;
    const destination = forwardTimeOf(next);
    setBackground(next);

    if (Math.abs(destination - position) < 0.02) return;

    const forward = destination > position;
    const desired = forward ? vid : vidBack;
    target = forward ? destination : backTime(destination);

    // Misma dirección: alcanza con mover el objetivo.
    if (desired === active) {
      if (active.paused) playActive();
      return;
    }

    // Cambio de dirección: sincronizar el otro video y recién ahí mostrarlo.
    active.pause();
    stopWatching();
    active = desired;
    const token = ++switchToken;
    const start = () => {
      if (token !== switchToken || disposed) return;
      show(desired);
      playActive();
    };
    const startAt = forward ? position : backTime(position);
    if (Math.abs(desired.currentTime - startAt) < 0.02) {
      start();
    } else {
      desired.addEventListener("seeked", start, { once: true });
      desired.currentTime = startAt;
    }
  };

  const onReady = () => {
    readyCount += 1;
    if (!isReady() || disposed) return;
    show(vid);
    if (pendingStation !== null) goTo(pendingStation);
    pendingStation = null;
  };

  // Los mp4 tienen el índice (moov) al final, así que se descargan completos
  // para que los saltos de tiempo sean instantáneos. Si falla, se usa la URL.
  const load = (video, url) => {
    video.addEventListener("loadedmetadata", onReady, { once: true });
    video.addEventListener("timeupdate", onTimeUpdate);
    const req = new XMLHttpRequest();
    req.open("GET", url, true);
    req.responseType = "blob";
    req.onload = () => {
      if (disposed) return;
      if (req.status === 200) {
        const objectUrl = URL.createObjectURL(req.response);
        objectUrls.push(objectUrl);
        video.src = objectUrl;
      } else {
        video.src = url;
      }
    };
    req.onerror = () => {
      if (!disposed) video.src = url;
    };
    req.send();
    requests.push(req);
  };

  setBackground(0);
  load(vid, URL_VID);
  load(vidBack, URL_VID_BACK);

  return {
    goTo,
    dispose() {
      disposed = true;
      stopWatching();
      requests.forEach((req) => req.abort());
      [vid, vidBack].forEach((v) => {
        v.removeEventListener("loadedmetadata", onReady);
        v.removeEventListener("timeupdate", onTimeUpdate);
        v.pause();
      });
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    },
  };
}

function Video(props) {
  const vidRef = useRef(null);
  const vidBackRef = useRef(null);
  const playerRef = useRef(null);
  const carouselRef = useRef(null);

  // 1-based: lo consume InfoCiclo / sobremaniinfo.
  const [currentPeanutPlantStatus, setCurrentPeanutPlantStatus] = useState(1);

  useEffect(() => {
    const player = createPlayer(vidRef.current, vidBackRef.current);
    playerRef.current = player;
    return () => {
      player.dispose();
      playerRef.current = null;
    };
  }, []);

  const goToStation = (station) => {
    const next = Math.min(Math.max(station, 0), LAST_STATION);
    setCurrentPeanutPlantStatus(next + 1);
    if (playerRef.current) playerRef.current.goTo(next);
  };

  const step = (delta) => {
    const next = currentPeanutPlantStatus - 1 + delta;
    if (next < 0 || next > LAST_STATION) return;
    if (carouselRef.current) {
      if (delta > 0) carouselRef.current.slideNext();
      else carouselRef.current.slidePrev();
    }
    goToStation(next);
  };

  return (
    <>
      <div className="container position-relative">
        <div id="divfadeout">
          <InfoCiclo currentPeanutPlantStatus={currentPeanutPlantStatus} />
        </div>
      </div>
      <div className="videoBox">
        <div className="video-position">
          <video
            className="video"
            id="video"
            ref={vidRef}
            muted
            playsInline
            preload="auto"
          />
          <video
            className="videoBack"
            id="videoBack"
            ref={vidBackRef}
            muted
            playsInline
            preload="auto"
          />
        </div>
        <img
          className="tierra d-none d-md-block"
          src="/assets/tierra.svg"
          alt="tierra"
        />
        <img
          className="tierra d-md-none"
          src="/assets/tierra-mobile.svg"
          alt="tierra"
        />

        <div id="days" className="">
          <div className="container">
            <div className="buttons" id="buttons">
              <button className="btnBack" id="btnBack" onClick={() => step(-1)}>
                <img src="/assets/arrow-left-ciclo.svg" alt="" />
              </button>
              <button className="btnFor" id="btnFor" onClick={() => step(1)}>
                <img src="/assets/arrow-right-ciclo.svg" alt="" />
              </button>
            </div>
          </div>
          <div className="w-100">
            <div className="w-100">
              <div className="container">
                <div className="position-relative">
                  <div className="specialDot">
                    <div className="goldDot"></div>
                  </div>
                </div>
              </div>
              <div className="lineTimeLine"></div>
              <div className="container">
                <div className="dots">
                  {Array.from({ length: DOTS_ALWAYS }, (_, i) => (
                    <p key={`dot-${i}`}>.</p>
                  ))}
                  {Array.from({ length: DOTS_DESKTOP }, (_, i) => (
                    <p key={`dot-md-${i}`} className="d-none d-md-block">
                      .
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <div className="container">
              <Carousel
                ref={carouselRef}
                focusOnSelect={true}
                showEmptySlots
                breakPoints={[
                  { width: 1, itemsToShow: 1, pagination: false },
                  {
                    width: 400,
                    itemsToShow: 3,
                    itemsToScroll: 1,
                    pagination: false,
                  },
                  {
                    width: 850,
                    itemsToShow: 5,
                    itemsToScroll: 1,
                    pagination: false,
                  },
                ]}
              >
                {DAYS.map(({ day, title }, index) => (
                  <div className="row" key={day}>
                    <button
                      className={`button-day ${
                        currentPeanutPlantStatus === index + 1
                          ? "focusBotton"
                          : ""
                      }`}
                      id={`day${index}`}
                      onClick={() => goToStation(index)}
                    >
                      <div className="rayita"></div>
                      {i18n.t("lyfecycle.day")} {day} <br />
                      <span>{i18n.t(title)}</span>
                    </button>
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Video;
